import React from 'react';
import { formatDateTime } from '../../lib/utils';
import { Badge } from '../../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import { ACTION_LABELS, SEVERITY_CONFIG } from './auditLogConstants';

const Field = ({ label, value, mono = false, fullWidth = false }) => (
  <div className={fullWidth ? 'col-span-2' : ''}>
    <label className="text-sm font-medium text-slate-500">{label}</label>
    <p className={mono ? 'font-mono text-sm break-all' : ''}>{value || '-'}</p>
  </div>
);

/**
 * Read-only modal dialog showing every field of a single audit-log entry,
 * including a formatted JSON dump of the `details` object when present.
 */
const AuditLogDetailDialog = ({ open, onOpenChange, log }) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto" data-testid="audit-log-detail">
      <DialogHeader>
        <DialogTitle>Audit Log Details</DialogTitle>
      </DialogHeader>
      {log && (
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <Field
              label="Timestamp"
              value={formatDateTime(log.timestamp)}
              mono
            />
            <div>
              <label className="text-sm font-medium text-slate-500">Severity</label>
              <div className="mt-1">
                <Badge className={SEVERITY_CONFIG[log.severity]?.color}>{log.severity}</Badge>
              </div>
            </div>
            <Field label="User" value={log.user_email || 'Anonymous'} />
            <Field label="User ID" value={log.user_id} mono />
            <Field label="Action" value={ACTION_LABELS[log.action] || log.action} />
            <Field label="Resource" value={log.resource_type} />
            <Field label="Method" value={log.method} />
            <Field label="Status Code" value={log.status_code} />
            <Field label="Path" value={log.path} mono fullWidth />
            <Field label="IP Address" value={log.ip_address} mono />
            <Field label="Duration" value={log.duration_ms ? `${log.duration_ms}ms` : null} />
            <div className="col-span-2">
              <label className="text-sm font-medium text-slate-500">User Agent</label>
              <p className="text-xs text-slate-600 break-all">{log.user_agent || '-'}</p>
            </div>
          </div>

          {log.details && Object.keys(log.details).length > 0 && (
            <div>
              <label className="text-sm font-medium text-slate-500">Additional Details</label>
              <pre className="mt-1 p-3 bg-slate-50 rounded-lg text-xs overflow-x-auto">
                {JSON.stringify(log.details, null, 2)}
              </pre>
            </div>
          )}

          <div className="pt-2 border-t border-slate-200">
            <label className="text-sm font-medium text-slate-500">Log ID</label>
            <p className="font-mono text-xs text-slate-400">{log.id}</p>
          </div>
        </div>
      )}
    </DialogContent>
  </Dialog>
);

export default AuditLogDetailDialog;
